import React, { useContext } from "react";

import CAVisibilityRankings from "./CAVisibilityRankings";
import AverageSentimentScores from "../../Component/BrandIndexHistory/AverageSentimentScores";
import PDFFooter from "../../Component/PDFFooter/PDFFooter";
import MUICard from "../../Component/UI/MUICard/MUICard";
import { OVERVIEW_LLM_LABELS } from "../../utils/constant";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import AttributesOverview from "../BrandAudit/CompAttributes/CompAttributes";
import CategoryDimensions from "../BrandAudit/CategoryDimensions/CategoryDimensions";
import CoreAttributes from "../BrandAudit/CoreAttributes/CoreAttributes";
import SourcesOverview from "../BrandAudit/Sources/sourcesOverview";
import { CategoryAnalysisContext } from "./category-analysis-context";
import CategoryScoreCards from "./CategoryScoreCards";

function CAPDFCategorySection({ category, competitors }) {
  const caCtx = useContext(CategoryAnalysisContext);
  const selectedLLM = caCtx.state.selectedLLM;
  const selectedMarket = caCtx.state?.selectedMarket;
  const selectedPersona = caCtx.state?.selectedPersona;
  const entity = caCtx.entity || category;

  // Visibility Data (Rankings) — always use the first attribute for PDF
  const caRankScoreExtras =
    caCtx.rank_scores[selectedLLM]?.[category]?.[selectedMarket]?.[selectedPersona]?.extras;
  const caProductMappingAttribute = caRankScoreExtras?.productMappingAttribute ?? {};
  const caProductRankingsArray = caRankScoreExtras?.productRankingsArray ?? [];
  const caProductRankingData = caRankScoreExtras?.productRankingsData ?? [];
  const attributeKeys = Object.keys(caProductMappingAttribute);
  const effectiveAttribute = attributeKeys[0] ?? null;
  const visibilityTitle =
    caCtx?.mapMetricTitle?.("Rank Data") ?? "Rank Data";

  const caReachData = (() => {
    if (!caProductRankingsArray.length || !effectiveAttribute) return null;
    const indices = caProductMappingAttribute[effectiveAttribute] ?? [];
    if (!indices.length) return null;
    return (
      <div data-report-section="reach" style={{ width: "100%" }}>
        <CAVisibilityRankings
          rankings={indices.map((i) => caProductRankingsArray[i])}
          productRankingDataSets={indices.map((i) => caProductRankingData[i] ?? null)}
          pIndices={indices}
          sectionIndices={indices.map((_, i) => i)}
        />
      </div>
    );
  })();

  // Sentinel value to check if sentiment data has arrived
  const brandData =
    caCtx.brand_scores[selectedLLM]?.[category]?.[selectedMarket]?.[selectedPersona]?.[1]?.extras;
  const hasSentimentData = !!(brandData?.sentimentMeta);
  const competitorNames = caCtx?.competitors?.[category]?.map((c) => c.name) ?? [];
  const hasCompetitors = (competitors[category]?.length ?? 0) > 0;
  const isPrimaryMarket = !selectedMarket || selectedMarket === caCtx.market;

  return (
    <div style={{ marginBottom: 32 }}>
      {/* Category header: name + metadata badges */}
      <MUICard className="p-4 mb-4 ca-pdf-section">
        <div>
          <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.3rem", color: "#1a1a2e" }}>
            {category}
          </h3>
          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {selectedLLM && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: "0.8rem",
                  color: "#555",
                  background: "#f0f4ff",
                  borderRadius: 6,
                  padding: "3px 10px",
                  border: "1px solid #d0d9ff",
                }}
              >
                <span style={{ fontWeight: 600 }}>LLM:</span>
                <span>{OVERVIEW_LLM_LABELS[selectedLLM] || selectedLLM}</span>
              </div>
            )}
            {selectedMarket && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: "0.8rem",
                  color: "#555",
                  background: "#f0faf4",
                  borderRadius: 6,
                  padding: "3px 10px",
                  border: "1px solid #b2dfce",
                }}
              >
                <span style={{ fontWeight: 600 }}>Market:</span>
                <span>
                  {selectedMarket === "GeneralMarket" ? "General Market" : selectedMarket}
                </span>
              </div>
            )}
            {selectedPersona && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: "0.8rem",
                  color: "#555",
                  background: "#fff8f0",
                  borderRadius: 6,
                  padding: "3px 10px",
                  border: "1px solid #ffd9b3",
                }}
              >
                <span style={{ fontWeight: 600 }}>Persona:</span>
                <span>{selectedPersona}</span>
              </div>
            )}
          </div>
        </div>
      </MUICard>

      {/* Category Score Cards */}
      <div className="ca-pdf-section">
        <CategoryScoreCards category={category} />
      </div>

      {/* Visibility Data */}
      {caReachData && (
        <MUICard className="p-4 mb-4 ca-pdf-section">
          <div>
            <h5 style={{ margin: 0 }}>{visibilityTitle}</h5>
          </div>
          <hr />
          {caReachData}
        </MUICard>
      )}

      {/* Average Sentiment Scores */}
      {hasSentimentData && (
        <MUICard className="p-4 mb-4 ca-pdf-section">
          <h5>{caCtx?.mapMetricTitle?.("Average Sentiment Scores") ?? "Average Sentiment Scores"}</h5>
          <hr />
          <AverageSentimentScores
            category={category}
            market={selectedMarket}
            selectedPersona={selectedPersona}
            selectedLLM={selectedLLM}
            competitors={competitors}
            ctx={caCtx}
          />
        </MUICard>
      )}

      {/* Positive / Negative Attributes */}
      {hasCompetitors && isPrimaryMarket && (
        <MUICard className="p-4 mb-4 ca-pdf-section">
          <h5>Positive and Negative Attributes</h5>
          <hr />
          <AttributesOverview
            category={category}
            isCategoryOverview={true}
            ctx={caCtx}
            filterBrands={competitorNames}
          />
        </MUICard>
      )}

      {/* Brand Pillars (CoreAttributes) */}
      {hasCompetitors && isPrimaryMarket && (
        <MUICard className="p-4 mb-4 ca-pdf-section">
          <h5>{ENTITY_LABELS.brandPillar}</h5>
          <hr />
          <CoreAttributes category={category} entity={entity} ctx={caCtx} />
        </MUICard>
      )}

      {/* Purchase Criteria (CategoryDimensions) */}
      {hasCompetitors && isPrimaryMarket && (
        <MUICard className="p-4 mb-4 ca-pdf-section">
          <h5>{ENTITY_LABELS.purchaseCriteria}</h5>
          <hr />
          <CategoryDimensions category={category} entity={entity} ctx={caCtx} module="categoryAnalysis" />
        </MUICard>
      )}

      {/* Sources — ca-pdf-sources-wrapper unsets height caps; ca-pdf-section
          keeps the whole block on one page where possible. */}
      <div className="ca-pdf-sources-wrapper ca-pdf-section">
        <MUICard className="p-4 mb-4">
          <SourcesOverview category={category} isCategoryOverview={false} ctx={caCtx} />
        </MUICard>
      </div>
    </div>
  );
}

/**
 * Full PDF content for CategoryAnalysis: one section per category tab.
 * Rendered in a hidden-on-screen / visible-in-print container via CSS.
 */
export default function CAPDFContent({ categories, competitors }) {
  return (
    <>
      {/* CA-specific PDF header — PDFHeader is kept untouched for Discovery */}
      <div
        className="print-only"
        style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", marginBottom: 8 }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <img src="/menu.png" style={{ width: 30, height: 30, margin: "0 10px" }} alt="logo" />
          <h2 style={{ margin: 0 }}>Revere: Brand Luminaire Category Analysis</h2>
        </div>
      </div>
      {categories?.map((cat, idx) => (
        <React.Fragment key={cat}>
          {idx > 0 && (
            <div
              style={{ pageBreakBefore: "always", breakBefore: "always", height: 0 }}
            />
          )}
          <CAPDFCategorySection category={cat} competitors={competitors} />
        </React.Fragment>
      ))}
      <PDFFooter />
    </>
  );
}
